/**
 * StepConfigure — Dynamic form rendered from manifest.config_fields[].
 *
 * Pre-fills values from browse selection (from_browse mapping).
 * Also includes filter config (extensions, max size) and sync config.
 */

import { useI18n } from '@/i18n';
import type { EnrichedProviderManifest, ConfigField } from '../../types';
import type { BrowseSelection } from '../browse/types';

interface StepConfigureProps {
  manifest: EnrichedProviderManifest;
  browseSelection: BrowseSelection | null;
  name: string;
  onNameChange: (name: string) => void;
  providerConfig: Record<string, unknown>;
  onProviderConfigChange: (config: Record<string, unknown>) => void;
  filterConfig: Record<string, unknown>;
  onFilterConfigChange: (config: Record<string, unknown>) => void;
  syncConfig: Record<string, unknown>;
  onSyncConfigChange: (config: Record<string, unknown>) => void;
}

export function StepConfigure({
  manifest,
  browseSelection,
  name,
  onNameChange,
  providerConfig,
  onProviderConfigChange,
  filterConfig,
  onFilterConfigChange,
  syncConfig,
  onSyncConfigChange,
}: StepConfigureProps) {
  const { t } = useI18n();

  // Config fields that are NOT auth fields
  const configFields = manifest.config_fields;

  const handleFieldChange = (key: string, value: unknown) => {
    onProviderConfigChange({ ...providerConfig, [key]: value });
  };

  // Resolve effective value: browse selection → current config → default
  const getFieldValue = (field: ConfigField): unknown => {
    // If this field has from_browse mapping and we have a browse selection
    if (field.from_browse && browseSelection?.config[field.from_browse] != null) {
      return browseSelection.config[field.from_browse];
    }
    if (providerConfig[field.key] != null) {
      return providerConfig[field.key];
    }
    return field.default ?? '';
  };

  return (
    <div className="max-w-lg mx-auto space-y-8">
      {/* Data source name */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          {t('datasources.wizard.configure.basicInfo')}
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('datasources.form.name')}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder={t('datasources.form.namePlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Provider-specific fields */}
      {configFields.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {t('datasources.wizard.configure.providerConfig')}
          </h3>
          <div className="space-y-4">
            {configFields.map((field) => (
              <DynamicField
                key={field.key}
                field={field}
                value={getFieldValue(field)}
                onChange={(v) => handleFieldChange(field.key, v)}
                disabled={!!field.from_browse && browseSelection?.config[field.from_browse] != null}
              />
            ))}
          </div>
        </div>
      )}

      {/* Filter config */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          {t('datasources.form.filterConfig')}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('datasources.form.extensions')}
            </label>
            <input
              type="text"
              value={(filterConfig.include_extensions as string[])?.join(', ') ?? '.pdf, .docx, .md, .txt'}
              onChange={(e) =>
                onFilterConfigChange({
                  ...filterConfig,
                  include_extensions: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                })
              }
              placeholder=".pdf, .docx, .md, .txt"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('datasources.form.extensionsHint')}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('datasources.form.maxFileSize')}
            </label>
            <input
              type="number"
              value={((filterConfig.max_file_size_bytes as number) ?? 104857600) / 1048576}
              onChange={(e) =>
                onFilterConfigChange({
                  ...filterConfig,
                  max_file_size_bytes: Math.max(1, Number(e.target.value)) * 1048576,
                })
              }
              min={1}
              max={500}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">MB</p>
          </div>
        </div>
      </div>

      {/* Sync config */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          {t('datasources.form.syncConfig')}
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('datasources.form.syncInterval')}
          </label>
          <input
            type="number"
            value={(syncConfig.interval_minutes as number) ?? 60}
            onChange={(e) =>
              onSyncConfigChange({
                ...syncConfig,
                interval_minutes: Math.max(15, Math.min(1440, Number(e.target.value))),
              })
            }
            min={15}
            max={1440}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('datasources.form.syncIntervalHint')}
          </p>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// DynamicField — Renders a single config field by type
// =============================================================================

function DynamicField({
  field,
  value,
  onChange,
  disabled,
}: {
  field: ConfigField;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled: boolean;
}) {
  const { t } = useI18n();
  const strValue = String(value ?? '');

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {t(field.label)}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {field.type === 'select' && field.options ? (
        <select
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60"
        >
          <option value="">{field.placeholder ?? ''}</option>
          {field.options.map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {String(opt.label ?? opt.value)}
            </option>
          ))}
        </select>
      ) : field.type === 'number' ? (
        <input
          type="number"
          value={strValue}
          onChange={(e) => onChange(Number(e.target.value))}
          placeholder={field.placeholder ?? ''}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60"
        />
      ) : (
        <input
          type={field.type === 'password' ? 'password' : 'text'}
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? ''}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60"
        />
      )}

      {field.hint && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t(field.hint)}</p>
      )}
    </div>
  );
}
